import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { AnonymousShell } from '../components/AnonymousShell'
import { Markdown } from '../components/Markdown'
import { SECTIONS } from '../schema/intakeSchema'
import {
  inQuestionnaire,
  questionAnchor,
  resolveLegacyAnchor,
  sectionAnchor,
} from '../export/intakeMarkdown'

/**
 * PUBLIC help for the emailed intake questionnaire (outside RequireAuth, like
 * /newcustomer). The Word questionnaire keeps each question's help to one line
 * and links here — `#executive-sponsor` — for the full explanation + examples.
 * A left rail (the intake `section-nav` idiom) lists every section and its
 * questions. Renders only the static question schema: no customer data, no
 * API calls.
 */
export function QuestionnaireHelp() {
  const { hash } = useLocation()

  // The anchor targets render synchronously, so scroll once they're mounted.
  // An old `#section.field` link (the first questionnaires) still resolves.
  useEffect(() => {
    if (!hash) return
    const id = resolveLegacyAnchor(decodeURIComponent(hash.slice(1)))
    document.getElementById(id)?.scrollIntoView()
  }, [hash])

  const sections = SECTIONS.filter(inQuestionnaire)
  const heading = (s: (typeof sections)[number]) =>
    s.number > 0 ? `${s.number}. ${s.title}` : s.title
  return (
    <AnonymousShell>
      <div
        className="split-layout split-layout--narrow"
        style={{ maxWidth: '72rem', margin: '0 auto', padding: '1rem 0 3rem' }}
        data-testid="questionnaire-help"
      >
        <nav className="sticky-sidebar" aria-label="Questionnaire sections" data-testid="qhelp-nav">
          <ul className="section-nav" style={{ maxHeight: 'calc(100vh - 6rem)', overflowY: 'auto' }}>
            {sections.map((section) => (
              <li key={section.key}>
                <a
                  className="section-nav__row"
                  href={`#${sectionAnchor(section.key)}`}
                  data-testid={`qhelp-nav-${sectionAnchor(section.key)}`}
                  style={{ fontWeight: 600, textDecoration: 'none', color: 'inherit' }}
                >
                  <span className="section-nav__title">{heading(section)}</span>
                </a>
                <ul className="section-nav">
                  {section.fields.map((field) => {
                    const id = questionAnchor(section.key, field.key)
                    return (
                      <li key={field.key}>
                        <a
                          className="section-nav__row"
                          href={`#${id}`}
                          style={{
                            paddingLeft: '1.25rem',
                            fontSize: '0.82rem',
                            textDecoration: 'none',
                            color: 'var(--muted)',
                          }}
                        >
                          <span className="section-nav__title">{field.label}</span>
                        </a>
                      </li>
                    )
                  })}
                </ul>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <h1 style={{ marginTop: 0 }}>Questionnaire help</h1>
          <p className="muted">
            Explanations and examples for each question in your assessment questionnaire.
          </p>
          {sections.map((section) => (
            <section key={section.key} id={sectionAnchor(section.key)}>
              <h2>{heading(section)}</h2>
              {section.fields.map((field) => {
                const id = questionAnchor(section.key, field.key)
                return (
                  <div
                    key={field.key}
                    id={id}
                    data-testid={`qhelp-${id}`}
                    style={{
                      borderTop: '1px solid var(--border)',
                      padding: '0.75rem 0',
                      scrollMarginTop: '1rem',
                    }}
                  >
                    <h3 style={{ margin: '0 0 0.35rem' }}>{field.label}</h3>
                    <p style={{ margin: '0 0 0.5rem' }}>{field.hint}</p>
                    {field.help && <Markdown text={field.help} compact />}
                  </div>
                )
              })}
            </section>
          ))}
        </div>
      </div>
    </AnonymousShell>
  )
}
